import { apiService } from './api.service'

export interface DMSUserProfile {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  date_joined: string
  is_staff: boolean
  is_superuser: boolean
  is_active: boolean
  groups: number[]
  user_permissions: string[]
  inherited_permissions: string[]
  auth_token?: string | null
  has_usable_password: boolean
  social_accounts: SocialAccount[]
  is_mfa_enabled: boolean
  has_auth_token?: boolean
  password?: string
}

export interface SocialAccount {
  id: number
  provider: string
  uid: string
  date_joined: string
}

export interface SocialAccountProvider {
  id: string
  name: string
  login_url: string
}

export interface TotpSettings {
  secret: string
  qr_svg: string // backend returns raw svg markup (like react expects)
  recovery_codes: string[]
}

class ProfileServiceClass {
  async get(): Promise<DMSUserProfile> {
    const response = await apiService.get<DMSUserProfile>('profile/')
    return response.data
  }

  async update(profile: Partial<DMSUserProfile>): Promise<DMSUserProfile> {
    const response = await apiService.patch<DMSUserProfile>('profile/', profile)
    return response.data
  }

  async getAuthToken(): Promise<{ token: string | null; hasToken: boolean }> {
    const response = await apiService.get<{ token: string | null; hasToken: boolean }>('profile/auth_token/')
    return response.data
  }

  async generateAuthToken(): Promise<string> {
    const response = await apiService.post<string>('profile/generate_auth_token/', {})
    return response.data
  }

  async disconnectSocialAccount(id: number): Promise<number> {
    const response = await apiService.post<number>('profile/disconnect_social_account/', { id })
    return response.data
  }

  async getSocialAccountProviders(): Promise<SocialAccountProvider[]> {
    const response = await apiService.get<SocialAccountProvider[]>('profile/social_account_providers/')
    return response.data
  }

  async getTotpSettings(): Promise<TotpSettings> {
    const response = await apiService.get<TotpSettings>('profile/totp/')
    return response.data
  }

  async activateTotp(totpSecret: string, totpCode: string): Promise<{ success: boolean; recovery_codes: string[] }> {
    const response = await apiService.post<{ success: boolean; recovery_codes: string[] }>('profile/totp/', {
      secret: totpSecret,
      code: totpCode,
    })
    return response.data
  }

  async deactivateTotp(): Promise<boolean> {
    const response = await apiService.delete<boolean>('profile/totp/', {})
    return response.data
  }
}

export const profileService = new ProfileServiceClass()
